/**
 * Preço da Vitrine em créditos — a tabela, num lugar só.
 *
 * O documento de origem manda: "implementar como constante tipada, não
 * hardcoded espalhado". Aqui mora, e daqui saem o resumo de custo do wizard, o
 * `custo_creditos` que a Edge Function grava no anúncio ao publicar, e a cota
 * grátis por plano. O banco NÃO recalcula nada: a aprovação consome o valor
 * gravado, e é isso que impede a tabela de mudar por baixo de um anúncio já
 * enviado para moderação.
 */

export const TIERS = ["simples", "destaque", "premium"] as const;
export type Tier = (typeof TIERS)[number];

export const DURACOES_DIAS = [7, 15, 30, 90] as const;
export type DuracaoDias = (typeof DURACOES_DIAS)[number];

/** Créditos por duração e tier. Linha = duração, coluna = tier. */
export const PRECO_CREDITOS: Record<DuracaoDias, Record<Tier, number>> = {
  7:  { simples: 1, destaque: 2,  premium: 4 },
  15: { simples: 2, destaque: 4,  premium: 7 },
  30: { simples: 3, destaque: 6,  premium: 12 },
  90: { simples: 7, destaque: 14, premium: 28 },
};

/** Fotos permitidas por tier. */
export const FOTOS_POR_TIER: Record<Tier, number> = {
  simples: 3,
  destaque: 8,
  premium: 15,
};

/** Rótulos de interface. O nome oficial de cada tier, como aparece na tela. */
export const TIER_LABEL: Record<Tier, string> = {
  simples: "Simples",
  destaque: "Destaque",
  premium: "Premium",
};

export function isTier(v: unknown): v is Tier {
  return typeof v === "string" && (TIERS as readonly string[]).includes(v);
}

export function isDuracao(v: unknown): v is DuracaoDias {
  return typeof v === "number" && (DURACOES_DIAS as readonly number[]).includes(v);
}

/**
 * Custo em créditos de um anúncio. Lança em combinação inválida — devolver 0
 * faria a moderação aprovar de graça um tier que não existe.
 */
export function custoEmCreditos(tier: Tier, duracaoDias: DuracaoDias): number {
  if (!isTier(tier)) throw new Error(`Tier inválido: ${String(tier)}`);
  if (!isDuracao(duracaoDias)) throw new Error(`Duração inválida: ${String(duracaoDias)}`);
  return PRECO_CREDITOS[duracaoDias][tier];
}

/**
 * Cota de anúncios ativos simultâneos SEM custo, por plano do GIFI.
 *
 * O documento citava "Essencial 1 · Profissional 3 · Premium 10 · Enterprise
 * ilimitado" — nomes que não existem neste produto. Os planos reais são
 * `free`, `premium` e `plus` (src/lib/plans.ts), e a cota foi mapeada para
 * eles preservando a intenção: quem paga mais anuncia mais de graça.
 * `Infinity` é ilimitado; `0` é "sem cota, tudo em crédito".
 */
/**
 * Anúncios da Vitrine que cada plano publica sem gastar crédito.
 *
 * A escada nova (Essencial, Profissional, Premium, Enterprise) recebe a mesma
 * cota do degrau equivalente na antiga — a planilha de preços fala de créditos
 * do GIFI Anunciar, não da Vitrine, então o número não foi inventado: foi
 * transportado. As chaves antigas ficam porque as contas de cortesia ainda usam.
 */
export const COTA_VITRINE_POR_PLANO: Record<string, number> = {
  free: 1,
  essential: 1,
  starter: 1,
  professional: 3,
  premium: 10,
  plus: 10,
  business: 10,
  enterprise: 10,
};

/**
 * O que a cota grátis COBRE, por plano. `null` = qualquer tier e duração.
 *
 * POR QUE ISTO EXISTE
 * -------------------
 *
 * A cota era contada só em anúncios ativos simultâneos, sem olhar tier nem
 * duração. Nos planos pagos isso é aceitável — quem paga pelo Premium tem dez
 * anúncios e escolhe o que quiser com eles. Na porta de entrada, não: uma cota
 * de 1 sem restrição entregaria um anúncio **Premium de 90 dias** de graça, que
 * custa 28 créditos — mais que o pacote de R$ 179.
 *
 * A cota do degrau de entrada existe para a pessoa EXPERIMENTAR: publicar,
 * receber o primeiro contato, e então decidir se compra crédito. Trinta dias no
 * tier Simples cumprem isso; sete não — numa vitrine que ainda está enchendo,
 * uma semana não prova nada, e a pessoa sai achando que não funciona.
 *
 * Para mudar o que a entrada ganha, mexa AQUI e no gate que cobra este número.
 */
export const COTA_LIMITE_POR_PLANO: Record<string, { tier: Tier; duracaoMaxDias: DuracaoDias } | null> = {
  free: { tier: "simples", duracaoMaxDias: 30 },
  essential: { tier: "simples", duracaoMaxDias: 30 },
  starter: { tier: "simples", duracaoMaxDias: 30 },
  professional: null,
  premium: null,
  plus: null,
  business: null,
  enterprise: null,
};

export function getCotaVitrine(plano: string | null | undefined): number {
  if (!plano) return 0;
  return COTA_VITRINE_POR_PLANO[plano] ?? 0;
}

/** O limite de tier/duração da cota daquele plano, ou `null` se não há limite. */
export function getLimiteDaCota(plano: string | null | undefined) {
  if (!plano) return null;
  return COTA_LIMITE_POR_PLANO[plano] ?? null;
}

/**
 * Decide se uma nova publicação cabe na cota grátis.
 *
 * `tier` e `duracaoDias` são OBRIGATÓRIOS de propósito: com eles opcionais, uma
 * chamada que os esquecesse cairia no caso permissivo e daria de graça o anúncio
 * mais caro do catálogo. Errar para o lado aberto, aqui, é receita perdida sem
 * ninguém notar — e este projeto já pagou por defeito que só se manifesta como
 * silêncio.
 */
export function cabeNaCota(
  plano: string | null | undefined,
  ativosAgora: number,
  tier: Tier,
  duracaoDias: DuracaoDias,
): boolean {
  const cota = getCotaVitrine(plano);
  if (cota !== Infinity && ativosAgora >= cota) return false;

  const limite = getLimiteDaCota(plano);
  if (!limite) return true;

  return tier === limite.tier && duracaoDias <= limite.duracaoMaxDias;
}
/* ─── Resolução de plano ─────────────────────────────────────────────────────
 *
 * De onde sai o plano de uma conta, e por que isto está escrito duas vezes.
 *
 * O QUE ESTAVA ERRADO
 * -------------------
 * A tela resolvia o plano pelo `price_id`, contra um mapa de 12 preços; o
 * servidor resolvia pelo `product_id`, contra um mapa de 4 produtos. A tela
 * aceitava assinatura `active` e `trialing`; o servidor aceitava também
 * `past_due`. Nenhum dos dois conhecia a escada v3.
 *
 * O efeito era de dinheiro: um assinante que a tela considerava pago podia ser
 * tratado como gratuito pelo servidor — e levar débito de até 28 créditos
 * depois de ler "Sem custo". E um inadimplente ganhava anúncio grátis do
 * servidor enquanto a tela lhe dizia que ia custar.
 *
 * COMO PASSA A SER
 * ----------------
 * Uma função pura, com o MESMO corpo dos dois lados, coberta pelo teste de
 * paridade que já cobre a tabela de preço. Ela não guarda identificador de
 * preço da escada nova: esses são do AMBIENTE — o de testes tem preço de teste,
 * a produção tem o dela —, e foi por isso que eles saíram do instantâneo
 * versionado. Quem informa é a tabela `plans` daquele banco, passada como
 * argumento.
 *
 * O mapa herdado abaixo é diferente: são preços de produção da escada v1/v2,
 * iguais nos dois ambientes, e existem só para não quebrar quem assinou antes.
 *
 * `past_due` FICA DE FORA, e essa é uma escolha, não um detalhe: incluí-lo dava
 * cota paga a quem parou de pagar, e contradizia o que a própria tela mostra à
 * pessoa. A regra do módulo de planos é errar para MENOS — mostrar "faça
 * upgrade" a quem podia publicar custa um clique; o contrário custa receita.
 */

/** As situações de assinatura que contam como plano vigente, nos dois lados. */
export const STATUS_DE_ASSINATURA_VIGENTE = ["active", "trialing"] as const;

/**
 * As situações em que a conta CONTINUA usando o produto.
 *
 * A diferença para a lista acima é `past_due`, e ela é deliberada.
 *
 * `past_due` significa "o Stripe tentou cobrar e ainda não conseguiu" — ele
 * repete a cobrança por dias e só então manda `canceled` ou `unpaid`. Derrubar
 * o acesso no primeiro cartão recusado tirava um cliente de 30 imóveis e o
 * punha em 1 no mesmo segundo, com "Assinatura Inativa" por cima dos
 * relatórios, enquanto a cobrança ainda estava em curso.
 *
 * **Acesso não é a mesma pergunta que gastar.** O que já foi contratado
 * continua durante a tentativa de cobrança; o que CUSTA — a cota gratuita da
 * Vitrine, que vira débito de crédito na aprovação — segue exigindo assinatura
 * em ordem, e por isso a Vitrine continua usando a lista de cima. Errar para
 * menos ali custa um clique; errar para mais custa receita.
 */
export const STATUS_DE_ASSINATURA_COM_ACESSO = ["active", "trialing", "past_due"] as const;

/** Preços da escada antiga (v1/v2). São de produção e iguais nos dois ambientes. */
export const PRECO_HERDADO_PARA_PLANO: Record<string, string> = {
  price_1T8pftD85maAQgQcNywqYqcw: "premium",
  price_1T8phiD85maAQgQcS4EM1MeI: "premium",
  price_1T8pgrD85maAQgQczZNpNyFI: "plus",
  price_1T8pijD85maAQgQc40BsEG0u: "plus",
  price_1TkvRqD85maAQgQctorlCVOJ: "starter",
  price_1TkvRsD85maAQgQcp0dk9pqw: "starter",
  price_1TkvRtD85maAQgQcKJaeOyTq: "plus",
  price_1TkvRuD85maAQgQcYGRbnIDX: "plus",
  price_1TkvRvD85maAQgQcRUjtnGsY: "plus",
  price_1TkvRwD85maAQgQcBTzvauTN: "plus",
  price_1TkvRyD85maAQgQcV9uTdwfr: "plus",
  price_1TkvRzD85maAQgQcg6kUJdjS: "plus",
};

/** Produtos da escada antiga, para assinatura que só trouxe `product_id`. */
export const PRODUTO_HERDADO_PARA_PLANO: Record<string, string> = {
  prod_U73ngnYHFuTyiL: "premium",
  prod_U73pfhWBTt5YT8: "premium",
  prod_U73oVveX6yhRgj: "plus",
  prod_U73qdzKmhdWSwu: "plus",
};

export type AssinaturaParaPlano = {
  status?: string | null;
  price_id?: string | null;
  product_id?: string | null;
  plan_key?: string | null;
  subscription_type?: string | null;
};

/** Uma linha de `plans`, com os identificadores de preço DAQUELE ambiente. */
export type PlanoDoCatalogo = {
  plan_key: string | null;
  stripe_price_id_monthly?: string | null;
  stripe_price_id_annual?: string | null;
};

/** Espaço reservado não é preço: quem não vendeu nada não identifica ninguém. */
function precoReal(id: string | null | undefined): string | null {
  if (!id || id.startsWith("STRIPE_PRICE_ID")) return null;
  return id;
}

/**
 * O plano de uma conta, na ordem em que cada fonte é confiável.
 *
 * @param sub      a assinatura vigente, ou nada
 * @param catalogo as linhas de `plans` do ambiente — é o que identifica a escada nova
 */
/**
 * @param situacoesAceitas quais situações contam como plano vigente.
 *
 * O padrão é a lista ESTRITA — sem `past_due` —, porque quem chama sem pensar
 * costuma estar decidindo gasto, e ali errar para mais custa receita. Quem
 * decide ACESSO passa `STATUS_DE_ASSINATURA_COM_ACESSO` explicitamente, e essa
 * escolha fica escrita na chamada em vez de escondida no padrão.
 */
export function planoDaAssinatura(
  sub: AssinaturaParaPlano | null | undefined,
  catalogo: PlanoDoCatalogo[] = [],
  situacoesAceitas: readonly string[] = STATUS_DE_ASSINATURA_VIGENTE,
): string {
  if (!sub) return "free";

  // Situação fora da lista não é plano vigente. Quem lê a assinatura já costuma
  // filtrar, mas a conferência aqui é o que garante os dois lados iguais mesmo
  // quando um dos chamadores esquece o filtro.
  if (sub.status && !situacoesAceitas.includes(sub.status)) {
    return "free";
  }

  // Cortesia e teste não passam pelo Stripe: o plano é o que foi concedido.
  if (sub.subscription_type && sub.subscription_type !== "paid" && sub.plan_key) {
    return sub.plan_key;
  }

  // A escada VIGENTE, pelos preços daquele ambiente.
  const preco = precoReal(sub.price_id);
  if (preco) {
    for (const p of catalogo) {
      if (!p.plan_key) continue;
      if (precoReal(p.stripe_price_id_monthly) === preco || precoReal(p.stripe_price_id_annual) === preco) {
        return p.plan_key;
      }
    }
    if (PRECO_HERDADO_PARA_PLANO[preco]) return PRECO_HERDADO_PARA_PLANO[preco];
  }

  if (sub.product_id && PRODUTO_HERDADO_PARA_PLANO[sub.product_id]) {
    return PRODUTO_HERDADO_PARA_PLANO[sub.product_id];
  }

  // Último recurso. Vale para assinatura semeada à mão, que existe nos dois
  // ambientes de teste e não tem preço nenhum.
  if (sub.plan_key) return sub.plan_key;

  return "free";
}
